import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { API_URL, enrichedFetch } from '../services/api';
import { PaginationMode } from '../Types/enums';
import { isPaginatedResult, PaginatedResult, PaginationMetadata } from '../Types/pagination';
import { buildUrlWithParams } from '../util/url';

export interface UseFetchDataProps {
  urlPath: string;
  queryParams?: Record<string, string>;
  skip?: boolean;
  paginated?: boolean;
  paginationMode?: PaginationMode;
  initialPage?: number;
  initialPageSize?: number;
}

// Return types
type BaseReturn = {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  startPolling: (intervalMs: number) => void;
  stopPolling: () => void;
};

type NonPaginatedReturn<T> = BaseReturn & {
  data: T | null;
};

type PaginatedReturn<T> = BaseReturn & {
  data: T[] | null;
  nextPage: () => void;
  prevPage: () => void;
  resetPagination: () => void;
  setPagination: (page: number, pageSize?: number) => void;
  pagination: PaginationMetadata | null;
};

// Overloads
export function useFetchData<T>(
  props: UseFetchDataProps & { paginated?: false }
): NonPaginatedReturn<T>;

export function useFetchData<T>(props: UseFetchDataProps & { paginated: true }): PaginatedReturn<T>;

/**
 * Custom React hook for fetching data from an API endpoint, supporting both paginated and non-paginated modes.
 *
 * @template T The type of the data to be fetched.
 * @param {Object} params - Hook parameters.
 * @param {string} params.urlPath - The API endpoint path (relative to API_URL).
 * @param {Record<string, any>} [params.queryParams] - Optional query parameters to include in the request.
 * @param {boolean} [params.skip] - If true, disables fetching.
 * @param {boolean} [params.paginated=false] - If true, enables paginated fetching.
 * @param {PaginationMode} [params.paginationMode=PaginationMode.REPLACE] - Pagination mode: 'REPLACE' or 'APPEND'.
 * @param {number} [params.initialPage=0] - Initial page index for paginated mode.
 * @param {number} [params.initialPageSize=10] - Initial page size for paginated mode.
 * @returns {NonPaginatedReturn<T> | PaginatedReturn<T>} Returns an object with data, loading, error, and utility functions.
 *
 * @example
 * // Non-paginated usage
 * const { data, loading, error, refetch } = useFetchData<User>({ urlPath: 'users/123' });
 *
 * @example
 * // Paginated usage
 * const {
 *   data: users,
 *   loading,
 *   error,
 *   nextPage,
 *   prevPage,
 *   pagination,
 * } = useFetchData<User>({
 *   urlPath: 'users',
 *   paginated: true,
 *   initialPage: 0,
 *   initialPageSize: 20,
 * });
 *
 * @remarks
 * - Provides polling controls (`startPolling`, `stopPolling`).
 * - In paginated mode, exposes pagination helpers and metadata.
 * - Uses `enrichedFetch` for API requests and displays errors via `enqueueSnackbar`.
 */
export function useFetchData<T>({
  urlPath,
  queryParams,
  skip,
  paginated = false,
  paginationMode = PaginationMode.REPLACE,
  initialPage = 0,
  initialPageSize = 10,
}: UseFetchDataProps): NonPaginatedReturn<T> | PaginatedReturn<T> {
  // Non-paginated
  const [data, setData] = useState<T | null>(null);
  // Paginated
  const [rawPage, setRawPage] = useState<PaginatedResult<T> | null>(null);
  const [items, setItems] = useState<T[]>([]);
  // Common
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const pollingInterval = useRef<NodeJS.Timeout | null>(null);

  // Construct URL with query params and pagination
  const urlWithParams = useMemo(
    () => buildUrlWithParams(urlPath, queryParams, paginated ? { page, pageSize } : undefined),
    [urlPath, queryParams, paginated, page, pageSize]
  );

  const fetchData = useCallback(
    async (path: string) => {
      if (skip) return;
      setLoading(true);
      setError(null);

      try {
        const response = await enrichedFetch(`${API_URL}/${path}`);

        if (paginated) {
          if (!isPaginatedResult<T>(response)) {
            throw new Error('Expected paginated response');
          }
          setRawPage(response);
          setItems((prev) => {
            // Append mode
            if (paginationMode === PaginationMode.APPEND && page > initialPage) {
              return [...prev, ...response.items];
            }
            // Replace mode
            return response.items;
          });
        } else {
          setData(response as T);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        enqueueSnackbar(message, { variant: 'error' });
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [paginated, paginationMode, page, initialPage, skip]
  );

  // Polling controls
  const stopPolling = useCallback(() => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
      pollingInterval.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (intervalMs: number) => {
      if (skip) return;
      stopPolling();
      pollingInterval.current = setInterval(() => {
        fetchData(urlWithParams);
      }, intervalMs);
    },
    [skip, fetchData, urlWithParams, stopPolling]
  );

  useEffect(() => {
    if (skip) return;
    fetchData(urlWithParams);
  }, [skip, urlWithParams, fetchData]);

  // Pagination helpers
  const nextPage = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const prevPage = useCallback(() => {
    setPage((p) => Math.max(0, p - 1));
  }, []);

  const resetPagination = useCallback(() => {
    setPage(initialPage);
    setItems([]);
  }, [initialPage]);

  const setPagination = useCallback(
    (newPage: number, newPageSize?: number) => {
      if (newPage === page && (newPageSize === undefined || newPageSize === pageSize)) return;

      if (newPageSize !== undefined && newPageSize !== pageSize) {
        setPageSize(newPageSize);
        setPage(initialPage);
        setItems([]);
        return;
      }

      if (newPage !== page) {
        setPage(newPage);
        if (paginationMode === PaginationMode.REPLACE) setItems([]);
      }
    },
    [page, pageSize, initialPage, paginationMode]
  );

  // Pagination metadata
  const pagination = useMemo(() => {
    if (!paginated || !rawPage) return null;

    const totalPages = Math.ceil(rawPage.total / pageSize);

    return {
      page,
      pageSize,
      total: rawPage.total,
      totalPages,
      hasNextPage: page + 1 < totalPages,
      hasPrevPage: page > 0,
    };
  }, [paginated, rawPage, page, pageSize]);

  if (paginated) {
    return {
      data: items,
      loading,
      error,
      refetch: () => fetchData(urlWithParams),
      startPolling,
      stopPolling,
      nextPage,
      prevPage,
      resetPagination,
      setPagination,
      pagination,
    };
  }

  return {
    data,
    loading,
    error,
    refetch: () => fetchData(urlWithParams),
    startPolling,
    stopPolling,
  };
}
